import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

/**
 * Target bands and their prize ladders.
 *
 * Somebody chasing ₹1,00,000 a month and somebody chasing ₹12,50,000 cannot be
 * handed the same rungs, so a challenge is split by the monthly revenue target
 * each person carries, and every band gets its own ladder.
 *
 * Two things this component enforces rather than merely explains:
 *
 *   The next band starts one rupee above the last. 15 OGD people sit on exactly
 *   ₹2,50,000, and two bands both containing that figure would pay them twice.
 *   The API rejects an overlap too, but by then Finance has typed a whole form.
 *
 *   Rung targets are CUMULATIVE. Rung 2 of ₹1,50,000 already contains rung 1's
 *   ₹1,00,000 — it is not "₹50,000 more" — so the helper text says so and the
 *   API refuses a rung that is not above the one before it.
 */
const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const emptyTier = () => ({ target: '', prize: '' });
const emptyBand = (from) => ({ from: from ?? '', to: '', tiers: [emptyTier()] });

export default function BandLadderBuilder({
  usesBands,
  canBandByTarget,
  errorField,
  bands,
  onChange,
  onToggleBands,
  isAdmission,
  disabled,
  roundNoun,
}) {
  const setBands = (next) => onChange(next);

  const patchBand = (index, patch) =>
    setBands(bands.map((b, i) => (i === index ? { ...b, ...patch } : b)));

  const patchTier = (bandIndex, tierIndex, patch) =>
    setBands(
      bands.map((b, i) =>
        i === bandIndex
          ? { ...b, tiers: b.tiers.map((t, j) => (j === tierIndex ? { ...t, ...patch } : t)) }
          : b,
      ),
    );

  /**
   * A new band starts one above the highest ceiling so far, so the common case
   * needs no typing and cannot overlap. Finance can still type over it.
   */
  const addBand = () => {
    const ceilings = bands
      .map((b) => Number(b.to))
      .filter((n) => Number.isFinite(n) && n > 0);
    const nextFloor = ceilings.length ? Math.max(...ceilings) + 1 : '';
    setBands([...bands, emptyBand(nextFloor)]);
  };

  const removeBand = (index) => setBands(bands.filter((_, i) => i !== index));

  const addTier = (bandIndex) =>
    patchBand(bandIndex, { tiers: [...bands[bandIndex].tiers, emptyTier()] });

  const removeTier = (bandIndex, tierIndex) =>
    patchBand(bandIndex, {
      tiers: bands[bandIndex].tiers.filter((_, j) => j !== tierIndex),
    });

  // "Amount" alone read as the prize's value rather than the figure to reach.
  // The form names the box its message is about; a ladder can run to dozens of
  // them, so pointing at the right one matters more here than anywhere else.
  const faulty = (key) => errorField === key;

  // A partitioned challenge climbs this ladder afresh every round, so the label
  // says per what — "₹20,000" alone would read as the whole challenge's figure.
  const targetLabel = `${isAdmission ? 'Target admissions' : 'Target amount'}${
    roundNoun ? ` per ${roundNoun}` : ''
  }`;
  const adornment = isAdmission
    ? undefined
    : { startAdornment: <InputAdornment position="start">₹</InputAdornment> };

  return (
    <Box>
      {/* Splitting by revenue target needs those targets to exist, which today
          means OGD and not a head role. Everywhere else the ladder still works,
          it is simply one ladder for everybody, so the choice is not offered. */}
      {canBandByTarget && (
        <FormControlLabel
          control={
            <Checkbox
              checked={!usesBands}
              onChange={(e) => onToggleBands(!e.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Typography variant="body2">
              Same targets for everyone{' '}
              <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                (ignore revenue targets)
              </Typography>
            </Typography>
          }
        />
      )}

      {bands.map((band, bandIndex) => (
        <React.Fragment key={bandIndex}>
        {bandIndex > 0 && <Divider />}
        <Box
          sx={{
            px: 1,
            py: 1.5,
            // Alternating ground instead of a border: several groups still read
            // as separate blocks, without four lines of chrome around each one.
            backgroundColor: bandIndex % 2 ? 'grey.50' : 'transparent',
          }}
        >
          {/* The heading only earns its place where groups are possible. With
              no checkbox above there is only ever one group, so "Everyone"
              labels a distinction that does not exist. */}
          {canBandByTarget && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">
                {usesBands ? `Target Group ${bandIndex + 1}` : 'Everyone'}
              </Typography>
              {usesBands && bands.length > 1 && (
                <Tooltip title="Remove this group" arrow>
                  <IconButton size="small" onClick={() => removeBand(bandIndex)} disabled={disabled}>
                    <Icon icon="mdi:trash-can-outline" width={17} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}

          {usesBands && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                type="number"
                label="Revenue target from"
                value={band.from}
                error={faulty(`band.${bandIndex}.from`)}
                onChange={(e) => patchBand(bandIndex, { from: e.target.value })}
                disabled={disabled}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
              <TextField
                fullWidth
                type="number"
                label="Revenue target to"
                placeholder="no limit"
                value={band.to}
                error={faulty(`band.${bandIndex}.to`)}
                onChange={(e) => patchBand(bandIndex, { to: e.target.value })}
                disabled={disabled}
                helperText="Leave empty for no upper limit"
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Stack>
          )}


          {/* No "Rung N" label on these rows: the rungs are in order on screen
              anyway, and the caption was stealing enough width that the amount
              itself could not be read. */}
          {band.tiers.map((tier, tierIndex) => (
            <Box key={tierIndex} sx={{ position: 'relative', mb: 2 }}>
              {/* Even halves, both full width. A prize is free text and an
                  amount is not, but the amount carries a currency prefix and
                  Indian grouping, so in practice they need the same room. */}
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <TextField
                  fullWidth
                  type="number"
                  label={targetLabel}
                  value={tier.target}
                  error={faulty(`band.${bandIndex}.tier.${tierIndex}.target`)}
                  onChange={(e) => patchTier(bandIndex, tierIndex, { target: e.target.value })}
                  disabled={disabled}
                  sx={{ flex: 1, minWidth: 0 }}
                  InputProps={adornment}
                />
                <TextField
                  fullWidth
                  label="Prize"
                  value={tier.prize}
                  error={faulty(`band.${bandIndex}.tier.${tierIndex}.prize`)}
                  onChange={(e) => patchTier(bandIndex, tierIndex, { prize: e.target.value })}
                  disabled={disabled}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Stack>

              {/* Sits ON the corner of the row like a notification badge rather
                  than taking a column of its own — that column was width the
                  two fields could use. Only once a second row exists: with one
                  row there is nothing to remove. */}
              {band.tiers.length > 1 && (
                <Tooltip title="Remove this row" arrow>
                  <IconButton
                    size="small"
                    onClick={() => removeTier(bandIndex, tierIndex)}
                    disabled={disabled}
                    sx={{
                      position: 'absolute',
                      top: -9,
                      right: -9,
                      zIndex: 2,
                      width: 20,
                      height: 20,
                      backgroundColor: 'error.main',
                      color: 'common.white',
                      boxShadow: 1,
                      '&:hover': { backgroundColor: 'error.dark' },
                    }}
                  >
                    <Icon icon="mdi:close" width={13} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}

          <Stack direction="row" justifyContent="center">
            <Tooltip title="Add a target and prize" arrow>
              <IconButton
                onClick={() => addTier(bandIndex)}
                disabled={disabled}
                sx={{ border: '1px dashed', borderColor: 'divider' }}
              >
                <Icon icon="mdi:plus" width={20} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        </React.Fragment>
      ))}

      {usesBands && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Typography
            component="button"
            type="button"
            variant="body2"
            onClick={addBand}
            disabled={disabled}
            sx={{
              border: 0,
              background: 'none',
              cursor: 'pointer',
              color: 'primary.main',
              fontWeight: 600,
            }}
          >
            + Add target group
          </Typography>
        </Stack>
      )}

      {usesBands && bands.some((b) => b.from !== '' && b.to !== '') && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          Anyone whose revenue target falls outside every group is left out of this challenge.
        </Typography>
      )}
    </Box>
  );
}

BandLadderBuilder.propTypes = {
  usesBands: PropTypes.bool.isRequired,
  /** Whether this assignment can be split by revenue target at all. */
  canBandByTarget: PropTypes.bool,
  /** Key of the single input the current error message is about. */
  errorField: PropTypes.string,
  bands: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onToggleBands: PropTypes.func.isRequired,
  isAdmission: PropTypes.bool,
  disabled: PropTypes.bool,
  /** "day", "week" or "round" when the ladder is climbed per round; absent otherwise. */
  roundNoun: PropTypes.string,
};

export { emptyBand, emptyTier, money };
